/**
 * The organization's apps in Home's sidebar, as Home lists them,
 * then an "All apps" row to the Apps overview.
 *
 * @module components/home/HomeSidebarApps
 */
import { useLocation } from "@tanstack/react-router";
import { LayoutGridIcon } from "lucide-react";
import { useMemo } from "react";

import { orgUrlSegment } from "../../orgRouting";
import { useActiveOrgId } from "../../state/orgs";
import { AppGlyph, AppLink, useAppEntries } from "../apps/AppParts";
import { homeSidebarApps } from "../apps/appRegistry";
import { appFromPathname } from "../apps/appsPaths";
import { SidebarMenu, SidebarMenuItem } from "../ui/sidebar";
import { SidebarRow } from "../ui/sidebar-row";
import { SidebarSection } from "../ui/sidebar-section";
import { HomeSidebarOverviewRow } from "./HomeSidebarOverviewRow";
import { useHomeSidebarSectionOpen } from "./HomeSidebarSection";

export function HomeSidebarApps({ onNavigate }: { readonly onNavigate: () => void }) {
  const orgId = useActiveOrgId();
  const pathname = useLocation({ select: (location) => location.pathname });
  const { entries } = useAppEntries(orgId);
  const apps = useMemo(() => homeSidebarApps(entries), [entries]);
  const [open, setOpen] = useHomeSidebarSectionOpen("apps");
  if (orgId === null) return null;
  const org = orgUrlSegment(orgId);
  const openApp = appFromPathname(pathname);
  return (
    <SidebarSection
      label="Apps"
      open={open}
      onOpenChange={setOpen}
    >
      <SidebarMenu className="gap-0.5 ps-px" data-home-apps="">
        {apps.map((entry) => {
          const route = entry.kind === "custom" ? `custom/${entry.id}` : entry.id;
          return (
            <SidebarMenuItem key={`${entry.kind}:${entry.id}`}>
              <SidebarRow
                data-home-app={entry.id}
                isActive={openApp === route || (openApp?.startsWith(`${route}/`) ?? false)}
                render={<AppLink entry={entry} orgSegment={org} onClick={onNavigate} />}
              >
                <AppGlyph entry={entry} />
                <span className="min-w-0 flex-1 truncate">{entry.name}</span>
              </SidebarRow>
            </SidebarMenuItem>
          );
        })}
        <HomeSidebarOverviewRow
          to="/$org/apps"
          org={org}
          icon={<LayoutGridIcon />}
          label="All apps"
          isActive={pathname === `/${org}/apps`}
          onNavigate={onNavigate}
        />
      </SidebarMenu>
    </SidebarSection>
  );
}
